import { AfterViewInit, ChangeDetectionStrategy, Component, inject, signal, ViewChild } from '@angular/core'

import { FormsModule } from '@angular/forms'
import { DynamicDialogConfig } from 'primeng/dynamicdialog'
import { Listbox, ListboxModule } from 'primeng/listbox'
import { TranslocoModule } from '@jsverse/transloco'
import { timer } from 'rxjs'
import { take } from 'rxjs/operators'
import { CareType } from '../../../../models/care-type.model'

// TODO: Remove this once we have a better names from the backend.
const LABEL_MAX_LENGTH = 30

export interface CareTypeSelectModalData {
  options: ReadonlyArray<CareType>
  selectedCareTypes: ReadonlyArray<CareType>
  careTypeIdsWithDuration: Set<number>
  confirmRemoveIfHasDuration: (careTypeId: number) => Promise<boolean>
  onSelectionChange?: (selection: CareType[]) => void
}

@Component({
  selector: 'app-care-type-select-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ListboxModule, TranslocoModule],
  templateUrl: './care-type-select-modal.component.html',
})
export class CareTypeSelectModalComponent implements AfterViewInit {
  @ViewChild('careTypeListbox') private readonly careTypeListbox: Listbox | undefined

  private readonly config = inject(DynamicDialogConfig)

  private get data(): CareTypeSelectModalData {
    return this.config.data as CareTypeSelectModalData
  }

  readonly options = signal<CareType[]>([])
  readonly selectedCareTypes = signal<CareType[]>([])
  readonly careTypeIdsWithDuration = new Set<number>()
  private confirmRemoveIfHasDuration!: (careTypeId: number) => Promise<boolean>

  constructor() {
    const d = this.data
    this.options.set([...d.options])
    this.selectedCareTypes.set([...d.selectedCareTypes])
    d.careTypeIdsWithDuration.forEach((id) => this.careTypeIdsWithDuration.add(id))
    this.confirmRemoveIfHasDuration = d.confirmRemoveIfHasDuration
  }

  ngAfterViewInit(): void {
    const listboxEl = this.careTypeListbox?.el?.nativeElement as HTMLElement | undefined
    const scrollContainer = (listboxEl?.querySelector('.p-listbox-list-container') ??
      listboxEl?.querySelector('.p-listbox-list-wrapper')) as HTMLElement | null
    const list = listboxEl?.querySelector('ul[role="listbox"]')

    const resetScroll = (): void => {
      if (scrollContainer) scrollContainer.scrollTop = 0
    }

    if (list && scrollContainer) {
      const onFirstFocus = (): void => {
        list.removeEventListener('focus', onFirstFocus)
        timer(0)
          .pipe(take(1))
          .subscribe(() => resetScroll())
      }
      list.addEventListener('focus', onFirstFocus)
    }

    timer(200)
      .pipe(take(1))
      .subscribe(() => resetScroll())
  }

  get selection(): CareType[] {
    return this.selectedCareTypes()
  }

  set selection(value: CareType[]) {
    this.selectedCareTypes.set(value ?? [])
  }

  async onSelectionChange(newSelection: CareType[]): Promise<void> {
    const previous = this.selectedCareTypes()
    const previousIds = new Set(previous.map((ct) => ct.id))
    const newIds = new Set((newSelection ?? []).map((ct) => ct.id))
    const removedIds = [...previousIds].filter((id) => !newIds.has(id))

    let allConfirmed = true
    for (const careTypeId of removedIds) {
      if (this.careTypeIdsWithDuration.has(careTypeId)) {
        const confirmed = await this.confirmRemoveIfHasDuration(careTypeId)
        if (!confirmed) {
          allConfirmed = false
          break
        }
      }
    }

    if (allConfirmed) {
      this.selectedCareTypes.set(newSelection ?? [])
      this.data.onSelectionChange?.(newSelection ?? [])
    } else {
      this.selectedCareTypes.set([...previous])
    }
  }

  truncateLabel(name: string | undefined): string {
    if (name == null) return ''
    return (name.length ?? 0) > LABEL_MAX_LENGTH ? name.slice(0, LABEL_MAX_LENGTH) + '...' : name
  }
}
